import { useState } from "react";
import { Link } from "react-router-dom";
import { Laptop, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMachines } from "@/hooks/queries/use-mailbox";
import { machineReadiness } from "@/lib/api/mailbox";

const DISMISS_KEY = "compbox.connectComputerBanner.dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Settings card: whether this account has a computer connected, and the way
 * to connect one (/connect). Without one, CompBox can't send work anywhere.
 */
export function ConnectComputerCard() {
  const { data: machines, isLoading } = useMachines();
  const count = machines?.length ?? 0;
  const online = machines?.filter((m) => machineReadiness(m) !== "offline").length ?? 0;

  let status: string;
  if (isLoading) status = "Checking…";
  else if (count === 0) status = "Not connected yet — it takes about 2 minutes.";
  else status = `${String(count)} computer${count === 1 ? "" : "s"} connected, ${String(online)} online.`;

  return (
    <div
      data-testid="connect-computer-card"
      className="glass-card mb-4 flex flex-col gap-3 rounded-2xl border border-white/[0.08] p-5 sm:flex-row sm:items-center sm:justify-between md:p-6"
    >
      <div className="flex items-start gap-3">
        <Laptop className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div>
          <h2 className="text-sm font-medium">Connect your computer</h2>
          <p className="text-sm text-muted-foreground">
            CompBox runs AI work on your own computer, with your own AI account. {status}
          </p>
        </div>
      </div>
      <Button asChild size="sm" variant={count === 0 ? "default" : "outline"} className="shrink-0">
        <Link to="/connect">{count === 0 ? "Connect a computer" : "Connect another"}</Link>
      </Button>
    </div>
  );
}

/**
 * Dashboard reminder for someone with no computer connected yet. Dismissible
 * (remembered in this browser); never shown while loading or on error.
 */
export function ConnectComputerBanner() {
  const { data: machines } = useMachines();
  const [dismissed, setDismissed] = useState(readDismissed);
  if (dismissed || !machines || machines.length > 0) return null;

  return (
    <div
      role="status"
      data-testid="connect-computer-banner"
      className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/[0.07] px-4 py-3 text-sm sm:flex-row sm:items-center"
    >
      <Laptop className="hidden h-4 w-4 shrink-0 text-primary sm:block" />
      <span className="flex-1">
        <strong>Connect your computer</strong> so CompBox can run AI work on it with your own AI account. It
        takes about 2 minutes.
      </span>
      <div className="flex items-center gap-2">
        <Button asChild size="sm">
          <Link to="/connect">Connect your computer</Link>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Dismiss"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, "1");
            } catch {
              // Private mode: dismissed for this visit only.
            }
            setDismissed(true);
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
